var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { saveState } from '../helper/stateManagerHelper';
// ColoredTiles render at a 110px logical cell.
var WORKSPACE_CELL_SIZE = 110;
// Padding outset on each side so the bounding box doesn't hug the tiles pixel-tight.
var PADDING = 12;
var ARROW_BASE = 10;
var ARROW_SHIFT = 50;
// Visual bounds (top-left + size) of a single member, in logical canvas coords.
var memberBox = function (m) {
    var _a, _b, _c, _d;
    if (m.data.type === 'linearPiece') {
        var d_1 = m.data;
        var scale_1 = (_a = d_1.scale) !== null && _a !== void 0 ? _a : 1;
        var dims = (_c = (_b = d_1.dimensions) !== null && _b !== void 0 ? _b : d_1.defaultDimensions) !== null && _c !== void 0 ? _c : { width: WORKSPACE_CELL_SIZE, height: 20 };
        var w_1 = dims.width * scale_1;
        var h_1 = dims.height * scale_1;
        return { left: d_1.center.x - w_1 / 2, top: d_1.center.y - h_1 / 2, w: w_1, h: h_1 };
    }
    var d = m.data;
    var scale = (_d = d.scale) !== null && _d !== void 0 ? _d : 1;
    var w = d.columns * WORKSPACE_CELL_SIZE * scale;
    var h = d.rows * WORKSPACE_CELL_SIZE * scale;
    return { left: d.center.x - w / 2, top: d.center.y - h / 2, w: w, h: h };
};
var computeBounds = function (members) {
    var minX = Infinity;
    var minY = Infinity;
    var maxX = -Infinity;
    var maxY = -Infinity;
    members.forEach(function (m) {
        var _a = memberBox(m), left = _a.left, top = _a.top, w = _a.w, h = _a.h;
        if (left < minX)
            minX = left;
        if (left + w > maxX)
            maxX = left + w;
        if (top < minY)
            minY = top;
        if (top + h > maxY)
            maxY = top + h;
    });
    if (!Number.isFinite(minX))
        return null;
    return {
        x: minX - PADDING,
        y: minY - PADDING,
        width: maxX - minX + PADDING * 2,
        height: maxY - minY + PADDING * 2,
    };
};
/**
 * Group overlay for grouped ColoredTiles (and any LinearPieces snapped into the
 * group). Mirrors NumberAndMoneyPieceSelectionGroup: one focusable bounding box
 * per group provides a SINGLE Tab stop + SINGLE highlight, and keeps the group
 * id selected (`setSelectedTool([groupId])`) so group-wide actions — scale
 * (handled in GameController), move, ungroup — operate on the whole group.
 * Members render individually elsewhere and own all mouse interaction; this
 * overlay only provides the group-level focus, highlight and keyboard move.
 */
var ColoredTilesSelectionGroup = function (_a) {
    var groupId = _a.groupId, members = _a.members;
    var t = useTranslation().t;
    var _b = useContext(DataContext), selectedTool = _b.selectedTool, setSelectedTool = _b.setSelectedTool, setManipulative = _b.setManipulative, setContextMenu = _b.setContextMenu;
    var isGroupActive = selectedTool.includes(groupId);
    var containerRef = useRef(null);
    var memberIds = useMemo(function () { return members.map(function (m) { return m.data.id; }); }, [members]);
    var bounds = useMemo(function () { return computeBounds(members); }, [members]);
    // Ref lets the keyboard handlers read the freshest member list without
    // re-binding listeners every commit.
    var membersRef = useRef(members);
    membersRef.current = members;
    // Ref lets the drag-follow listener below (registered once per groupId, not
    // per selectedTool change) read the current ad-hoc selection without going stale.
    var selectedToolRef = useRef(selectedTool);
    selectedToolRef.current = selectedTool;
    // -- Live-follow the group drag -----------------------------------------
    // Member tiles move during a group drag via direct DOM writes (see
    // ColoredTiles.tsx's multiSelectMove listener) — no React state update, and
    // therefore no re-render of this overlay, until drop. Without this, the
    // dashed bounding box stayed frozen mid-drag and only snapped into place
    // once setManipulative committed on mouseup. Mirror the same direct-DOM
    // delta so the box tracks the tiles live.
    var dragStartBoundsRef = useRef(null);
    useEffect(function () {
        var onStart = function () {
            var el = containerRef.current;
            dragStartBoundsRef.current = el
                ? { x: parseFloat(el.style.left) || 0, y: parseFloat(el.style.top) || 0 }
                : null;
        };
        var onMove = function (_a) {
            var deltaX = _a.deltaX, deltaY = _a.deltaY, srcGroupId = _a.groupId;
            var matchesGroupId = srcGroupId === groupId;
            // The drag leader may be an item that carries no groupId at all (e.g. a Dice
            // dragged alongside this group in an ad-hoc multi-selection) — in that case
            // multiSelectMove's payload never has a matching groupId, but the box should
            // still track the group's own members moving via their own follower listeners.
            var isAdHocMultiSelect = selectedToolRef.current.includes(groupId) && selectedToolRef.current.length > 1;
            if (!matchesGroupId && !isAdHocMultiSelect)
                return;
            var el = containerRef.current;
            var start = dragStartBoundsRef.current;
            if (!el || !start)
                return;
            el.style.left = "".concat(start.x + deltaX, "px");
            el.style.top = "".concat(start.y + deltaY, "px");
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
        };
    }, [groupId]);
    // Build a multipleCompleteData undo entry from a center-delta applied to a
    // start snapshot — restores every member's full data on a single Ctrl/Cmd+Z.
    var saveGroupMove = function (startItems, dx, dy) {
        if (dx === 0 && dy === 0)
            return;
        var newItems = startItems.map(function (m) {
            var c = m.data.center;
            return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: { x: c.x + dx, y: c.y + dy } }) });
        });
        saveState({
            type: 'multipleManipulatives',
            id: groupId,
            propertyName: 'multipleCompleteData',
            old: JSON.stringify(startItems),
            new: JSON.stringify(newItems),
        });
    };
    // -- Arrow-key move ----------------------------------------------------
    var arrowMoveBatchRef = useRef(null);
    var flushArrowMove = function () {
        var batch = arrowMoveBatchRef.current;
        if (!batch)
            return;
        saveGroupMove(batch.startItems, batch.dx, batch.dy);
        arrowMoveBatchRef.current = null;
    };
    var moveByArrow = function (dx, dy) {
        if (!arrowMoveBatchRef.current) {
            arrowMoveBatchRef.current = {
                startItems: membersRef.current.map(function (m) { return structuredClone(m); }),
                dx: 0,
                dy: 0,
                timer: null,
            };
        }
        var batch = arrowMoveBatchRef.current;
        batch.dx += dx;
        batch.dy += dy;
        if (batch.timer)
            clearTimeout(batch.timer);
        batch.timer = setTimeout(flushArrowMove, 500);
        var memberIdSet = new Set(memberIds);
        setManipulative(function (prev) {
            return prev.map(function (item) {
                var sid = item.data.id;
                if (!memberIdSet.has(sid))
                    return item;
                var c = item.data.center;
                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: c.x + dx, y: c.y + dy } }) });
            });
        });
    };
    var onKeyDown = function (e) {
        var _a;
        if (e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            e.stopPropagation();
            var rect = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect)
                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: groupId });
            return;
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            flushArrowMove();
            e.currentTarget.blur();
            return;
        }
        var step = e.shiftKey ? ARROW_SHIFT : ARROW_BASE;
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            moveByArrow(step, 0);
        }
        else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            moveByArrow(-step, 0);
        }
        else if (e.key === 'ArrowDown') {
            e.preventDefault();
            moveByArrow(0, step);
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            moveByArrow(0, -step);
        }
    };
    // -- Auto-focus on group creation --------------------------------------
    // GameController dispatches `focusPsGroup` for every newly created group
    // (keyed by groupId). Reuse it to move keyboard focus onto this overlay.
    useEffect(function () {
        var onFocusGroup = function (payload) {
            if ((payload === null || payload === void 0 ? void 0 : payload.groupId) !== groupId)
                return;
            setTimeout(function () { var _a; return (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 60);
        };
        customEventHandler.addEventListener('focusPsGroup', onFocusGroup);
        return function () { return customEventHandler.removeEventListener('focusPsGroup', onFocusGroup); };
    }, [groupId]);
    // -- onFocus: promote the group into selectedTool ----------------------
    // This keeps the manipulativeGroup_ id selected so GameController's group
    // scale / move / ungroup branches operate on the whole group. Fires on
    // keyboard Tab focus (pointer-events:none below doesn't block tab focus).
    var onFocus = function () {
        if (!selectedTool.includes(groupId))
            setSelectedTool([groupId]);
    };
    if (!bounds)
        return null;
    var ariaLabel = t('text.coloredTilesGroupAriaLabel', {
        count: members.length,
        defaultValue: "Group of ".concat(members.length, " colored tiles"),
    });
    // pointer-events:none so the overlay never steals mouse interaction from the
    // tiles beneath — the individual tiles own all mouse click/drag (group drag
    // via their multiSelectMove path). The overlay is purely the group's visual
    // highlight + single keyboard Tab stop (Tab focus is unaffected by
    // pointer-events:none).
    var wrapperStyle = {
        position: 'absolute',
        left: bounds.x,
        top: bounds.y,
        width: bounds.width,
        height: bounds.height,
        pointerEvents: 'none',
    };
    return (_jsx("div", { ref: containerRef, className: "coloredTilesSelectionGroup".concat(isGroupActive ? ' active' : ''), "data-manipulative-id": groupId, "data-group-active": isGroupActive ? 'true' : 'false', tabIndex: 0, role: "region", "aria-label": ariaLabel, style: wrapperStyle, onFocus: onFocus, onKeyDown: onKeyDown }));
};
export default ColoredTilesSelectionGroup;
